import axios from 'axios'
import { 
    userLoginRequest,
    userLoginSuccess,
    userLoginFail,

    userLogoutRequest,
    userLogoutSuccess,
    userLogoutFail,

    userRegisterRequest,
    userRegisterSuccess,
    userRegisterFail,

    userDetailsRequest,
    userDetailsSuccess,
    userDetailsFail,

    userUpdateProfileRequest,
    userUpdateProfileSuccess,
    userUpdateProfileFail,

    userDeleteRequest,
    userDeleteSuccess,
    userDeleteFail,

    userVerifyRequest,
    userVerifySuccess,
    userVerifyFail,

    userSendVerifyEmailRequest,
    userSendVerifyEmailSuccess,
    userSendVerifyEmailFail,
} from '../reducers/userReducers'

const createAPIinstance = (includeAuth = true) => {
    const token = localStorage.getItem('accessToken')
    
    return axios.create({
        baseURL: `${process.env.REACT_APP_BASE_API_URL}/api/users`,
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            ...(includeAuth && token && { 'Authorization': `Bearer ${token}` })
        },
        withCredentials: true
    })
}

// Function to get CSRF token from cookie
const getCsrfToken = () => {
    const name = 'csrftoken';
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
}

export const login = (email, password, turnstileToken) => async (dispatch) => {
    try {
        dispatch(userLoginRequest())
        const clientID = process.env.REACT_APP_MAIN_AUTH_CID
        const clientSecret = process.env.REACT_APP_MAIN_AUTH_CSECRET

        // Create instance without auth for login
        const api = createAPIinstance(false);
        
        // Ensure turnstileToken is a string and not null/undefined
        if (!turnstileToken || typeof turnstileToken !== 'string') {
            throw new Error('Invalid Turnstile token');
        }

        // First, make a GET request to get the CSRF token
        try {
            const csrfResponse = await api.get('/csrf/', {
                withCredentials: true
            });
        } catch (error) {
            console.error('Failed to get CSRF token:', error);
            throw new Error('Failed to get CSRF token');
        }

        // Get the CSRF token from cookies
        const csrfToken = getCsrfToken();
        if (!csrfToken) {
            throw new Error('CSRF token not found in cookies');
        }

        // Create form data
        const formData = new URLSearchParams();
        formData.append('grant_type', 'password');
        formData.append('username', email);
        formData.append('password', password);
        formData.append('client_id', clientID);
        formData.append('client_secret', clientSecret);
        formData.append('turnstile_token', turnstileToken);

        const { data } = await api.post(
            '/login/',
            formData,
            {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'X-CSRFToken': csrfToken
                },
                withCredentials: true
            }
        )

        // Store user data and access token
        localStorage.setItem('user', JSON.stringify(data.user))
        localStorage.setItem('accessToken', data.access_token)
        
        // Dispatch success actions
        dispatch(userLoginSuccess(data.user))
        dispatch(userDetailsSuccess(data.user))
    } catch(error) {
        // Log the full error for debugging
        console.error('Login error details:', {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            headers: error.response?.headers
        });
        
        // Extract error message
        let errorMessage = 'An error occurred during login';
        if (error.response?.data) {
            if (error.response.data.error_description) {
                errorMessage = error.response.data.error_description;
            } else if (error.response.data.detail) {
                errorMessage = error.response.data.detail;
            } else if (error.response.data.error) {
                errorMessage = error.response.data.error;
            }
        }
        
        dispatch(userLoginFail(errorMessage));
    }
}


export const logout = () => async (dispatch) => {
    try {
        dispatch(userLogoutRequest())

        // Get CSRF token for secure logout
        const csrfToken = getCsrfToken();
        
        const api = createAPIinstance();
        const { data } = await api.post(
            '/logout/',
            {},  // Empty body
            {
                headers: {
                    'X-CSRFToken': csrfToken
                },
                withCredentials: true
            }
        );
        
        // Signal success but don't affect UI - that's already handled
        dispatch(userLogoutSuccess(data))
    } catch(error) {
        console.error('Logout error:', error);
        
        // Only report the error, don't try to handle state here
        dispatch(userLogoutFail(
            error.response && error.response.data.detail
                ? error.response.data.detail
                : error.message
        ));
    }
}


export const detail = () => async (dispatch) => {
    try{
        dispatch(userDetailsRequest())

        const api = createAPIinstance();
        const { data } = await api.get('/profile/')

        dispatch(userDetailsSuccess(data))
        localStorage.setItem('user', JSON.stringify(data))
    }catch(error){
        dispatch(userDetailsFail(error.response && error.response.data.detail
                ? error.response.data.detail
                : error.message,
                )
        );
    }
}


export const register = (name, email, password, turnstileToken) => async (dispatch) => {
    try {
        dispatch(userRegisterRequest())

        // Create instance without auth for registration
        const api = createAPIinstance(false);
        
        // Ensure turnstileToken is a string and not null/undefined
        if (!turnstileToken || typeof turnstileToken !== 'string') {
            throw new Error('Invalid Turnstile token');
        }
        
        // First, make a GET request to get the CSRF token
        try {
            const csrfResponse = await api.get('/csrf/', {
                withCredentials: true
            });
        } catch (error) {
            throw new Error('Failed to get CSRF token');
        }

        // Get the CSRF token from cookies
        const csrfToken = getCsrfToken();
        if (!csrfToken) {
            throw new Error('CSRF token not found in cookies');
        }

        // Create the request payload
        const requestData = { 
            name, 
            email, 
            password,
            turnstile_token: turnstileToken 
        };

        const { data } = await api.post(
            '/register/',
            requestData,
            {
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken
                },
                withCredentials: true
            }
        )

        dispatch(userRegisterSuccess(data))
        
        // After successful registration, we don't immediately login
        // The user must first verify their email
    } catch (error) {
        console.error('Registration error details:', {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            headers: error.response?.headers
        });
        
        dispatch(userRegisterFail(
            error.response && error.response.data.detail
                ? error.response.data.detail
                : error.message
        ))
    }
}


export const update = (user) => async (dispatch) => {
    try{
        dispatch(userUpdateProfileRequest())

        const api = createAPIinstance();
        const { data } = await api.put(
            '/profile/',
            user
        )

        dispatch(userUpdateProfileSuccess(data))
        localStorage.setItem('user', JSON.stringify(data))
    }catch(error){
        dispatch(userUpdateProfileFail(error.response && error.response.data.detail
                ? error.response.data.detail
                : error.message,
                )
        );
    }
}


export const deleteUser = (password) => async (dispatch) => {
    try{
        dispatch(userDeleteRequest())

        const api = createAPIinstance();
        const { data } = await api.post(
            '/delete/',
            {
                'password': password
            }
        )
        
        dispatch(userDeleteSuccess(data))

        // Clear user data from localStorage
        localStorage.removeItem('user')
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
    }catch(error){
        dispatch(userDeleteFail(error.response && error.response.data.detail
                ? error.response.data.detail
                : error.message,
                )
        );
    }
}


export const verifyUser = (uidb64, token) => async (dispatch) => {
    try {
        dispatch(userVerifyRequest())

        const api = createAPIinstance()
        
        const response = await api.get(`/verify/${uidb64}/${token}/`)
        const data = response.data

        dispatch(userVerifySuccess(data.detail))

        // If verification is successful, automatically log in the user
        if (data.user) {
            dispatch(userLoginSuccess(data.user))
            localStorage.setItem('userInfo', JSON.stringify(data.user))
        }

    } catch (error) {
        dispatch(userVerifyFail(
            error.response && error.response.data.detail
                ? error.response.data.detail
                : error.message
        ))
    }
}


export const resendEmail = (email) => async (dispatch) => {
    try {
        dispatch(userSendVerifyEmailRequest())

        const api = createAPIinstance()
        const { data } = await api.post('/activate/resend/', {email})

        dispatch(userSendVerifyEmailSuccess(data))
    } catch (error) {
        dispatch(userSendVerifyEmailFail(
            error.response && error.response.data.detail ?
            error.response.data.detail :
            error.message
        ))
    }
}
